// ADR-0144 §4 — documentos del proyecto (core-api
// /asset-groups/:id/documents/*). Subir es presign → PUT directo al
// bucket → registrar; core-api re-verifica el objeto (existe, es PDF, pesa
// lo permitido) antes de crear la fila. Retirar no borra: marca
// superseded_at.

import {
  documentDownloadUrlResponseSchema,
  documentRetireResponseSchema,
  documentUploadUrlResponseSchema,
  projectDocumentSchema,
  projectDocumentsResponseSchema,
  type DocumentCreateRequest,
  type DocumentDownloadUrlResponse,
  type DocumentRetireResponse,
  type DocumentUploadUrlRequest,
  type DocumentUploadUrlResponse,
  type ProjectDocument,
  type ProjectDocumentsResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

const base = (groupId: string) => `/asset-groups/${encodeURIComponent(groupId)}/documents`;

export const documentsApi = {
  list: async (groupId: string): Promise<ProjectDocumentsResponse> => {
    const json = await request<unknown>("GET", "core", base(groupId));
    return projectDocumentsResponseSchema.parse(json);
  },
  uploadUrl: async (
    groupId: string,
    body: DocumentUploadUrlRequest
  ): Promise<DocumentUploadUrlResponse> => {
    const json = await request<unknown>("POST", "core", `${base(groupId)}/upload-url`, { body });
    return documentUploadUrlResponseSchema.parse(json);
  },
  // `idempotencyKey` obligatorio (p11): publicar es permanente. La
  // pantalla la mantiene estable por intento de subida, así que un
  // reintento tras perder la respuesta recibe el mismo documento.
  create: async (
    groupId: string,
    body: DocumentCreateRequest,
    idempotencyKey: string
  ): Promise<ProjectDocument> => {
    const json = await request<unknown>("POST", "core", base(groupId), {
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    });
    return projectDocumentSchema.parse(json);
  },
  retire: async (groupId: string, documentId: string): Promise<DocumentRetireResponse> => {
    const json = await request<unknown>(
      "POST",
      "core",
      `${base(groupId)}/${encodeURIComponent(documentId)}/retire`
    );
    return documentRetireResponseSchema.parse(json);
  },
  url: async (groupId: string, documentId: string): Promise<DocumentDownloadUrlResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `${base(groupId)}/${encodeURIComponent(documentId)}/url`
    );
    return documentDownloadUrlResponseSchema.parse(json);
  },
};

/** PUT directo al bucket con la URL presignada. Los headers firmados
 *  (Content-Type, Content-Length) van tal cual; sin Authorization. */
export async function putToStorage(presign: DocumentUploadUrlResponse, file: Blob): Promise<void> {
  const res = await fetch(presign.uploadUrl, {
    method: "PUT",
    body: file,
    headers: presign.requiredHeaders,
  });
  if (!res.ok) {
    throw new Error(`La subida al almacenamiento falló (${res.status}). Intenta de nuevo.`);
  }
}
